// Acquisition panel — listing details, the offer slider, and closing
// costs. Mirrors src/components/AcquisitionPanel.jsx in the React
// standalone, including the slider's "% of list price" readout and the
// RTL drag direction so left = lower offer.
//
// The underlying field is still `priceReductionPct` (0–30) per the
// math contract — the inversion is purely presentational.

import {computeDealMetrics} from '../calculations';
import {IDealLineItem, IDealState} from '../defaults';
import {captureFocusKey, currencyInput, numInput, panelSection, restoreFocusKey} from '../primitives';

export interface IAcquisitionPanelHost {
	getState(): IDealState;
	update(patch: Partial<IDealState>): void;
}

export class AcquisitionPanel {
	private rootEl: HTMLElement | null;
	private titleEl: HTMLElement | null = null;
	private subtitleEl: HTMLElement | null = null;
	private listPriceInput: HTMLInputElement | null = null;
	private listingUrlInput: HTMLInputElement | null = null;
	private reductionSlider: HTMLInputElement | null = null;
	private reductionReadoutEl: HTMLElement | null = null;
	private reductionHintEl: HTMLElement | null = null;
	private tickLabelEls: Array<HTMLElement> = [];
	private purchasePriceInput: HTMLInputElement | null = null;
	private purchaseSubscriptEl: HTMLElement | null = null;
	private purchaseDerivedBadge: HTMLElement | null = null;
	private specsEl: HTMLElement | null = null;
	private sqftField: HTMLElement | null = null;
	private bedsField: HTMLElement | null = null;
	private bathsField: HTMLElement | null = null;
	private lotAcresField: HTMLElement | null = null;
	private sqftInput: HTMLInputElement | null = null;
	private bedsInput: HTMLInputElement | null = null;
	private bathsInput: HTMLInputElement | null = null;
	private lotAcresInput: HTMLInputElement | null = null;
	private closingCostsListEl: HTMLElement | null = null;
	private closingRows: Map<string, {row: HTMLElement; weightEl: HTMLElement; input: HTMLInputElement}> = new Map();
	private closingTotalEl: HTMLElement | null = null;
	private totalAcqEl: HTMLElement | null = null;

	constructor(private host: IAcquisitionPanelHost) {
		this.rootEl = document.getElementById('id_lb-deal-panel-acquisition');
		if (!this.rootEl) {
			return;
		}
		this.scaffold();
		this.render();
	}

	render(): void {
		if (!this.rootEl) {
			return;
		}
		const focus = captureFocusKey(this.rootEl);
		const state = this.host.getState();
		const m = computeDealMetrics(state);
		const listPrice = state.listPrice || 0;
		const hasListPrice = listPrice > 0;
		const reductionPct = state.priceReductionPct || 0;
		const offerPct = 100 - reductionPct;

		if (this.titleEl) {
			this.titleEl.textContent = state.mode === 'build' ? 'Acquisition (Land)' : 'Acquisition';
		}
		if (this.subtitleEl) {
			this.subtitleEl.textContent = 'Listing details, your offer, and closing costs';
		}
		if (this.listPriceInput && document.activeElement !== this.listPriceInput) {
			this.listPriceInput.value = listPrice ? Number(listPrice).toLocaleString('en-US') : '';
		}
		if (this.listingUrlInput && document.activeElement !== this.listingUrlInput) {
			this.listingUrlInput.value = state.property.listingUrl || '';
		}
		if (this.reductionSlider) {
			if (document.activeElement !== this.reductionSlider) {
				this.reductionSlider.value = String(reductionPct);
			}
			this.reductionSlider.disabled = !hasListPrice;
		}
		if (this.reductionReadoutEl) {
			this.reductionReadoutEl.textContent = `${offerPct.toFixed(1)}%`;
		}
		if (this.reductionHintEl) {
			this.reductionHintEl.textContent = hasListPrice
				? 'Drag left to negotiate down. Floor at 70% of list.'
				: 'Enter a list price to enable.';
		}
		// Tick labels (70%, 85%, 100% — show $ amounts when listPrice > 0)
		const ticks: Array<[string, number]> = [['70%', 0.7], ['85%', 0.85], ['100% · full ask', 1.0]];
		this.tickLabelEls.forEach((el, i) => {
			const [label, mult] = ticks[i];
			if (hasListPrice && i < 2) {
				el.textContent = `${label} · ${formatCurrency(listPrice * mult)}`;
			} else {
				el.textContent = label;
			}
		});

		if (this.purchasePriceInput && document.activeElement !== this.purchasePriceInput) {
			this.purchasePriceInput.value = m.purchasePrice ? Number(m.purchasePrice).toLocaleString('en-US') : '';
		}
		if (this.purchaseDerivedBadge) {
			this.purchaseDerivedBadge.style.display = hasListPrice ? '' : 'none';
		}
		if (this.purchaseSubscriptEl) {
			if (hasListPrice && reductionPct > 0) {
				const reductionAmount = Math.round(listPrice * (reductionPct / 100));
				this.purchaseSubscriptEl.textContent =
					`${formatCurrency(listPrice)} − ${formatCurrency(reductionAmount)} (${offerPct.toFixed(1)}% of list)`;
				this.purchaseSubscriptEl.style.display = '';
			} else {
				this.purchaseSubscriptEl.style.display = 'none';
			}
		}

		this.renderSpecs(state);
		this.renderClosingCosts(state, m.closingCostsComputed, m.totalClosingCosts);
		if (this.totalAcqEl) {
			this.totalAcqEl.textContent = formatCurrency(m.totalAcquisition);
		}
		restoreFocusKey(this.rootEl, focus);
	}

	private renderSpecs(state: IDealState): void {
		const isBuild = state.mode === 'build';
		if (this.sqftField) {
			this.sqftField.style.display = isBuild ? 'none' : '';
		}
		if (this.bedsField) {
			this.bedsField.style.display = isBuild ? 'none' : '';
		}
		if (this.bathsField) {
			this.bathsField.style.display = isBuild ? 'none' : '';
		}
		if (this.lotAcresField) {
			this.lotAcresField.style.display = isBuild ? '' : 'none';
		}
		this.setInputValue(this.sqftInput, state.property.sqft || 0);
		this.setInputValue(this.bedsInput, state.property.beds || 0);
		this.setInputValue(this.bathsInput, state.property.baths || 0);
		this.setInputValue(this.lotAcresInput, state.property.lotAcres || 0);
	}

	// Closing-cost rows persist across renders so user typing isn't
	// destroyed mid-keystroke. Match by item id; create new rows on
	// first sight, update values in place, remove rows whose item id
	// is no longer in state.closingCosts.
	private renderClosingCosts(state: IDealState, computed: Array<IDealLineItem & {computed: number}>, total: number): void {
		if (!this.closingCostsListEl) {
			return;
		}
		const presentIds = new Set<string>();
		computed.forEach(c => {
			presentIds.add(c.id);
			let row = this.closingRows.get(c.id);
			if (!row) {
				row = this.scaffoldClosingRow(c);
				this.closingRows.set(c.id, row);
				this.closingCostsListEl!.appendChild(row.row);
			}
			if (c.weight > 0) {
				row.weightEl.textContent = `${(c.weight * 100).toFixed(2)}%`;
				row.weightEl.style.display = '';
			} else {
				row.weightEl.style.display = 'none';
			}
			this.setInputValue(row.input, c.computed);
		});
		for (const [id, row] of this.closingRows.entries()) {
			if (!presentIds.has(id)) {
				row.row.remove();
				this.closingRows.delete(id);
			}
		}
		if (this.closingTotalEl) {
			this.closingTotalEl.textContent = formatCurrency(total);
		}
	}

	private scaffoldClosingRow(c: IDealLineItem & {computed: number}): {row: HTMLElement; weightEl: HTMLElement; input: HTMLInputElement} {
		const row = document.createElement('div');
		row.className = 'lb-deal-line-row';
		const label = document.createElement('span');
		label.className = 'lb-deal-line-row__label';
		label.textContent = c.label;
		row.appendChild(label);
		const weightEl = document.createElement('span');
		weightEl.className = 'lb-deal-line-row__hint';
		row.appendChild(weightEl);
		const inputWrap = currencyInput({
			value: c.computed,
			size: 'small',
			focusKey: `acq-closing-${c.id}`,
			onChange: v => {
				const stateNow = this.host.getState();
				const next = (stateNow.closingCosts || []).map(line =>
					line.id === c.id ? {...line, amount: Math.max(0, v), weight: 0} : line);
				this.host.update({closingCosts: next});
			},
		});
		const input = inputWrap.querySelector('input') as HTMLInputElement;
		row.appendChild(inputWrap);
		return {row, weightEl, input};
	}

	private setInputValue(input: HTMLInputElement | null, value: number): void {
		if (!input || document.activeElement === input) {
			return;
		}
		input.value = value > 0 ? Number(value).toLocaleString('en-US', {maximumFractionDigits: 4}) : '';
	}

	// User edits the purchase-price field directly. When listPrice > 0,
	// back-solve priceReductionPct (clamped 0–30) so the slider stays
	// authoritative; when listPrice is 0, write purchasePrice directly.
	private handlePurchasePriceEdit(v: number): void {
		const state = this.host.getState();
		const listPrice = state.listPrice || 0;
		if (listPrice <= 0) {
			this.host.update({purchasePrice: Math.max(0, v)});
			return;
		}
		if (v >= listPrice) {
			this.host.update({priceReductionPct: 0});
			return;
		}
		const pct = Math.min(30, Math.max(0, ((listPrice - v) / listPrice) * 100));
		this.host.update({priceReductionPct: parseFloat(pct.toFixed(1))});
	}

	private scaffold(): void {
		if (!this.rootEl) {
			return;
		}
		const handles = panelSection({
			rootEl: this.rootEl,
			icon: 'attach_money',
			title: 'Acquisition',
			subtitle: 'Listing details, your offer, and closing costs',
		});
		this.titleEl = handles.titleEl;
		this.subtitleEl = handles.subtitleEl;
		const body = handles.bodyEl;

		// Listing block — list price + listing URL + reduction slider
		const listingBlock = document.createElement('div');
		listingBlock.className = 'lb-deal-listing-block';

		const listingGrid = document.createElement('div');
		listingGrid.className = 'lb-deal-grid lb-deal-grid--2';

		const listPriceField = document.createElement('div');
		listPriceField.className = 'lb-deal-field';
		const listPriceLabel = document.createElement('label');
		listPriceLabel.className = 'lb-deal-field__label';
		listPriceLabel.textContent = 'List price (public ask)';
		listPriceField.appendChild(listPriceLabel);
		const listPriceWrap = currencyInput({
			value: 0,
			onChange: v => {
				this.host.update({listPrice: Math.max(0, v)});
				this.render();
			},
		});
		this.listPriceInput = listPriceWrap.querySelector('input');
		listPriceField.appendChild(listPriceWrap);
		listingGrid.appendChild(listPriceField);

		const urlField = document.createElement('div');
		urlField.className = 'lb-deal-field';
		const urlLabel = document.createElement('label');
		urlLabel.className = 'lb-deal-field__label';
		urlLabel.textContent = 'Listing URL (reference)';
		urlField.appendChild(urlLabel);
		const urlInput = document.createElement('input');
		urlInput.type = 'url';
		urlInput.placeholder = 'https://...';
		urlInput.className = 'lb-deal-field__input';
		urlInput.addEventListener('input', () => {
			const state = this.host.getState();
			this.host.update({property: {...state.property, listingUrl: urlInput.value}});
		});
		this.listingUrlInput = urlInput;
		urlField.appendChild(urlInput);
		listingGrid.appendChild(urlField);

		listingBlock.appendChild(listingGrid);

		// Reduction slider — RTL so dragging left lowers the offer
		const reductionWrap = document.createElement('div');
		reductionWrap.className = 'lb-deal-reduction';
		const reductionHead = document.createElement('div');
		reductionHead.className = 'lb-deal-reduction__head';
		const reductionLabels = document.createElement('div');
		const reductionTitle = document.createElement('div');
		reductionTitle.className = 'lb-deal-reduction__title';
		reductionTitle.textContent = 'Offer as % of list price';
		reductionLabels.appendChild(reductionTitle);
		this.reductionHintEl = document.createElement('div');
		this.reductionHintEl.className = 'lb-deal-reduction__hint';
		reductionLabels.appendChild(this.reductionHintEl);
		reductionHead.appendChild(reductionLabels);
		this.reductionReadoutEl = document.createElement('div');
		this.reductionReadoutEl.className = 'lb-deal-reduction__readout';
		this.reductionReadoutEl.textContent = '100.0%';
		reductionHead.appendChild(this.reductionReadoutEl);
		reductionWrap.appendChild(reductionHead);

		const reductionSlider = document.createElement('input');
		reductionSlider.type = 'range';
		reductionSlider.min = '0';
		reductionSlider.max = '30';
		reductionSlider.step = '0.5';
		reductionSlider.className = 'lb-deal-reduction__slider';
		reductionSlider.style.direction = 'rtl';
		reductionSlider.addEventListener('input', () => {
			const v = Math.max(0, Math.min(30, parseFloat(reductionSlider.value) || 0));
			this.host.update({priceReductionPct: v});
			this.render();
		});
		this.reductionSlider = reductionSlider;
		reductionWrap.appendChild(reductionSlider);

		const tickRow = document.createElement('div');
		tickRow.className = 'lb-deal-reduction__ticks';
		this.tickLabelEls = [];
		['70%', '85%', '100% · full ask'].forEach(text => {
			const tick = document.createElement('span');
			tick.textContent = text;
			tickRow.appendChild(tick);
			this.tickLabelEls.push(tick);
		});
		reductionWrap.appendChild(tickRow);

		listingBlock.appendChild(reductionWrap);
		body.appendChild(listingBlock);

		// Purchase price (your offer)
		const purchaseField = document.createElement('div');
		purchaseField.className = 'lb-deal-field';
		const purchaseLabel = document.createElement('label');
		purchaseLabel.className = 'lb-deal-field__label';
		const purchaseLabelText = document.createElement('span');
		purchaseLabelText.textContent = 'Purchase price (your offer)';
		purchaseLabel.appendChild(purchaseLabelText);
		this.purchaseDerivedBadge = document.createElement('span');
		this.purchaseDerivedBadge.className = 'lb-deal-badge lb-deal-badge--derived';
		this.purchaseDerivedBadge.textContent = 'derived';
		this.purchaseDerivedBadge.style.display = 'none';
		purchaseLabel.appendChild(this.purchaseDerivedBadge);
		purchaseField.appendChild(purchaseLabel);

		const purchaseWrap = currencyInput({
			value: 0,
			size: 'large',
			onChange: v => {
				this.handlePurchasePriceEdit(Math.max(0, v));
				this.render();
			},
		});
		this.purchasePriceInput = purchaseWrap.querySelector('input');
		purchaseField.appendChild(purchaseWrap);

		this.purchaseSubscriptEl = document.createElement('div');
		this.purchaseSubscriptEl.className = 'lb-deal-field__caption';
		this.purchaseSubscriptEl.style.display = 'none';
		purchaseField.appendChild(this.purchaseSubscriptEl);
		body.appendChild(purchaseField);

		// Property specs (sqft/beds/baths in reno; lotAcres in build).
		// All four fields are scaffolded once and shown/hidden via
		// renderSpecs based on mode — this keeps user typing alive
		// across renders, since destroying-and-recreating the input
		// would erase partial decimal entries like "0.5" before the
		// user finishes typing.
		this.specsEl = document.createElement('div');
		this.specsEl.className = 'lb-deal-grid lb-deal-grid--3';
		const sqftField = this.makePersistentNumField('Sqft', 'acq-sqft', v => {
			const stateNow = this.host.getState();
			this.host.update({property: {...stateNow.property, sqft: v}});
		});
		this.sqftField = sqftField.wrap;
		this.sqftInput = sqftField.input;
		this.specsEl.appendChild(this.sqftField);
		const bedsField = this.makePersistentNumField('Beds', 'acq-beds', v => {
			const stateNow = this.host.getState();
			this.host.update({property: {...stateNow.property, beds: v}});
		});
		this.bedsField = bedsField.wrap;
		this.bedsInput = bedsField.input;
		this.specsEl.appendChild(this.bedsField);
		const bathsField = this.makePersistentNumField('Baths', 'acq-baths', v => {
			const stateNow = this.host.getState();
			this.host.update({property: {...stateNow.property, baths: v}});
		});
		this.bathsField = bathsField.wrap;
		this.bathsInput = bathsField.input;
		this.specsEl.appendChild(this.bathsField);
		const lotAcresField = this.makePersistentNumField('Lot size (acres)', 'acq-lotacres', v => {
			const stateNow = this.host.getState();
			this.host.update({property: {...stateNow.property, lotAcres: v}});
		});
		this.lotAcresField = lotAcresField.wrap;
		this.lotAcresInput = lotAcresField.input;
		this.specsEl.appendChild(this.lotAcresField);
		body.appendChild(this.specsEl);

		// Closing costs — collapsible breakdown
		const closingDetails = document.createElement('details');
		const closingSummary = document.createElement('summary');
		closingSummary.className = 'lb-deal-collapse__summary';
		closingSummary.textContent = 'Closing costs breakdown';
		this.closingTotalEl = document.createElement('span');
		this.closingTotalEl.className = 'lb-deal-collapse__total';
		this.closingTotalEl.textContent = '$0';
		closingSummary.appendChild(this.closingTotalEl);
		closingDetails.appendChild(closingSummary);
		this.closingCostsListEl = document.createElement('div');
		this.closingCostsListEl.className = 'lb-deal-line-list';
		closingDetails.appendChild(this.closingCostsListEl);
		body.appendChild(closingDetails);

		// Total acquisition footer
		const totalRow = document.createElement('div');
		totalRow.className = 'lb-deal-line-row lb-deal-line-row--summary';
		const totalLabel = document.createElement('span');
		totalLabel.className = 'lb-deal-line-row__label';
		totalLabel.textContent = 'Total acquisition';
		this.totalAcqEl = document.createElement('span');
		this.totalAcqEl.className = 'lb-deal-line-row__value';
		this.totalAcqEl.textContent = '$0';
		totalRow.appendChild(totalLabel);
		totalRow.appendChild(this.totalAcqEl);
		body.appendChild(totalRow);
	}

	private fieldRow(labelText: string, prefix: string, configure: (input: HTMLInputElement) => void): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-field';
		const label = document.createElement('label');
		label.className = 'lb-deal-field__label';
		label.textContent = labelText;
		wrap.appendChild(label);
		const inputRow = document.createElement('div');
		inputRow.className = 'lb-deal-field__input-row';
		if (prefix) {
			const prefixEl = document.createElement('span');
			prefixEl.className = 'lb-deal-field__prefix';
			prefixEl.textContent = prefix;
			inputRow.appendChild(prefixEl);
		}
		const input = document.createElement('input');
		input.className = 'lb-deal-field__input';
		configure(input);
		inputRow.appendChild(input);
		wrap.appendChild(inputRow);
		return wrap;
	}

	// Scaffold-once num field that returns both the wrapper and the
	// inner input. Caller stores both refs to update value (when not
	// focused) and toggle visibility on mode changes — so the input
	// element survives across renders and partial decimal typing
	// stays alive.
	private makePersistentNumField(labelText: string, focusKey: string, onChange: (v: number) => void): {wrap: HTMLElement; input: HTMLInputElement} {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-field';
		const label = document.createElement('label');
		label.className = 'lb-deal-field__label';
		label.textContent = labelText;
		wrap.appendChild(label);
		const input = numInput({
			value: 0,
			focusKey,
			onChange: v => onChange(Math.max(0, v)),
		});
		wrap.appendChild(input);
		return {wrap, input};
	}

	private numField(labelText: string, value: number, onChange: (v: number) => void, focusKey?: string): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = 'lb-deal-field';
		const label = document.createElement('label');
		label.className = 'lb-deal-field__label';
		label.textContent = labelText;
		wrap.appendChild(label);
		const input = numInput({
			value,
			focusKey,
			onChange: v => onChange(Math.max(0, v)),
		});
		wrap.appendChild(input);
		return wrap;
	}
}

function formatCurrency(n: number): string {
	if (!Number.isFinite(n)) {
		return '$0';
	}
	return n.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});
}
